<template>
  <div class="mm-request-view">
    <div class="form-container">
      <div class="form-header">
        <img src="/assets/img/logos/mattermost.png" alt="Mattermost logo" class="header-logo">
        <h1>Request to Join Mattermost</h1>
        <p>Fill out the form below and a Space Coast STEM admin will verify your request with your teacher</p>
      </div>

      <div v-if="successMessage" class="success-message">
        {{ successMessage }}
      </div>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <form v-if="!successMessage" @submit.prevent="submitRequest" class="mm-form">
        <!-- Student Information -->
        <section class="form-section">
          <h2>Your Information</h2>
          <div class="form-row">
            <div class="form-group">
              <label for="first_name">First Name *</label>
              <input
                id="first_name"
                v-model="formData.first_name"
                type="text"
                required
                placeholder="First name"
              >
            </div>
            <div class="form-group">
              <label for="last_name">Last Name *</label>
              <input
                id="last_name"
                v-model="formData.last_name"
                type="text"
                required
                placeholder="Last name"
              >
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="grade_level">Grade Level *</label>
              <select id="grade_level" v-model="formData.grade_level" required>
                <option value="">Select grade level...</option>
                <option value="4th">4th</option>
                <option value="5th">5th</option>
                <option value="6th">6th</option>
                <option value="7th">7th</option>
                <option value="8th">8th</option>
                <option value="9th">9th</option>
                <option value="10th">10th</option>
                <option value="11th">11th</option>
                <option value="12th">12th</option>
              </select>
            </div>
            <div class="form-group">
              <label for="student_email">Your Email *</label>
              <input
                id="student_email"
                v-model="formData.student_email"
                type="email"
                required
                placeholder="your-email@example.com"
              >
            </div>
          </div>
        </section>

        <!-- Parent/Guardian Info -->
        <section class="form-section">
          <h2>Parent / Guardian</h2>
          <p class="section-description">
            We require a parent or guardian on file. They will receive a notification about this request.
          </p>
          <div class="form-row">
            <div class="form-group">
              <label for="parent_name">Parent/Guardian Name *</label>
              <input
                id="parent_name"
                v-model="formData.parent_name"
                type="text"
                required
                placeholder="Full name"
              >
            </div>
            <div class="form-group">
              <label for="parent_email">Parent/Guardian Email *</label>
              <input
                id="parent_email"
                v-model="formData.parent_email"
                type="email"
                required
                placeholder="parent-email@example.com"
              >
            </div>
          </div>
        </section>

        <!-- Teacher Verification -->
        <section class="form-section">
          <h2>Teacher Verification</h2>
          <p class="section-description">
            Provide the email of a teacher who can confirm you are a current student.
            We'll reach out to them before approving your account.
          </p>

          <div class="form-row">
            <div class="form-group">
              <label for="school">School *</label>
              <select
                id="school"
                v-model="formData.school"
                required
                @change="onSchoolChange"
              >
                <option value="">-- Select School --</option>
                <option v-for="school in schools" :key="school.id" :value="school.id">
                  {{ school.name }}
                </option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="form-group">
              <label for="teacher_select">Teacher</label>
              <select
                id="teacher_select"
                v-model="selectedTeacher"
                :disabled="!formData.school || formData.school === 'other' || loadingTeachers"
                @change="onTeacherSelect"
              >
                <option value="">{{ loadingTeachers ? 'Loading teachers...' : '-- Select Teacher or Enter Email Below --' }}</option>
                <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
                  {{ teacher.first_name.charAt(0) }}. {{ teacher.last_name }}
                </option>
                <option value="other">Other (enter email below)</option>
              </select>
            </div>
          </div>

          <div class="form-group" v-if="showEmailInput">
            <label for="teacher_email">Teacher's Email *</label>
            <input
              id="teacher_email"
              v-model="formData.teacher_email"
              type="email"
              required
              placeholder="teacher@school.edu"
            >
          </div>
        </section>

        <!-- Acknowledgement -->
        <section class="form-section">
          <h2>Acknowledgement</h2>
          <label class="acknowledge-label">
            <input type="checkbox" v-model="formData.acknowledged" required>
            <span>
              I understand that Mattermost is monitored, that there is a zero-tolerance policy for bullying,
              and that my parent/guardian may review my activity at any time.
            </span>
          </label>
        </section>

        <div class="form-actions">
          <button type="submit" :disabled="submitting || !formData.acknowledged" class="btn-submit">
            {{ submitting ? 'Submitting...' : 'Submit Request' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { useSchools } from '@/composables/useSchools'

const { schools, loadSchools } = useSchools()

interface Teacher {
  id: string
  first_name: string
  last_name: string
  email: string
}

const formData = ref({
  first_name: '',
  last_name: '',
  grade_level: '',
  student_email: '',
  parent_name: '',
  parent_email: '',
  school: '',
  teacher_email: '',
  acknowledged: false
})

const teachers = ref<Teacher[]>([])
const selectedTeacher = ref('')
const loadingTeachers = ref(false)
const showEmailInput = ref(true)
const submitting = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const loadTeachers = async (schoolId: string) => {
  if (!schoolId || schoolId === 'other') {
    teachers.value = []
    return
  }
  loadingTeachers.value = true
  try {
    const response = await axios.get(`/api/v1/schools/${schoolId}/teachers`)
    teachers.value = response.data
  } catch (error) {
    console.error('Error loading teachers:', error)
    teachers.value = []
  } finally {
    loadingTeachers.value = false
  }
}

const onSchoolChange = () => {
  selectedTeacher.value = ''
  formData.value.teacher_email = ''
  showEmailInput.value = true
  if (formData.value.school && formData.value.school !== 'other') {
    loadTeachers(formData.value.school)
  } else {
    teachers.value = []
  }
}

const onTeacherSelect = () => {
  if (selectedTeacher.value && selectedTeacher.value !== 'other') {
    const teacher = teachers.value.find(t => t.id === selectedTeacher.value)
    if (teacher) {
      formData.value.teacher_email = teacher.email
      showEmailInput.value = false
    }
  } else {
    formData.value.teacher_email = ''
    showEmailInput.value = true
  }
}

const submitRequest = async () => {
  submitting.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const response = await axios.post('/api/v1/mattermost/request', formData.value)
    successMessage.value = response.data.message
      || 'Thanks! Your request has been submitted. We will contact your teacher to verify and follow up by email.'

    formData.value = {
      first_name: '',
      last_name: '',
      grade_level: '',
      student_email: '',
      parent_name: '',
      parent_email: '',
      school: '',
      teacher_email: '',
      acknowledged: false
    }
    selectedTeacher.value = ''
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (error: any) {
    console.error('Error submitting Mattermost request:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to submit request. Please try again.'
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  loadSchools()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Inter:wght@400;500;600&display=swap');

.mm-request-view {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem 1rem;
}

.form-container {
  max-width: 900px;
  margin: 0 auto;
  background: var(--white, #ffffff);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  padding: 2rem;
  text-align: center;
  color: var(--white, #ffffff);
}

.header-logo {
  width: 60px;
  height: 60px;
  background: var(--white, #ffffff);
  border-radius: 12px;
  padding: 8px;
  margin-bottom: 1rem;
}

.form-header h1 {
  margin: 0 0 0.5rem 0;
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
}

.form-header p {
  margin: 0;
  opacity: 0.95;
  font-family: 'Inter', sans-serif;
}

.success-message {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 1.5rem 2rem;
  margin: 2rem;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 1.1rem;
  line-height: 1.6;
}

.error-message {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 1rem 1.5rem;
  margin: 1.5rem 1.5rem 0 1.5rem;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
}

.mm-form {
  padding: 2rem;
}

.form-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #e0e0e0;
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.3rem;
  color: var(--primary-color, #2c4a6b);
  margin: 0 0 0.5rem 0;
}

.section-description {
  color: var(--text-light, #666666);
  margin: 0 0 1rem 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  line-height: 1.5;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-dark, #333333);
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  transition: border-color 0.3s;
  background: var(--white, #ffffff);
  color: var(--text-dark, #333333);
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--secondary-color, #3ba3d1);
}

.acknowledge-label {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-family: 'Inter', sans-serif;
  line-height: 1.5;
  color: var(--text-dark, #333333);
  cursor: pointer;
}

.acknowledge-label input[type="checkbox"] {
  width: 20px;
  height: 20px;
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: var(--secondary-color, #3ba3d1);
  cursor: pointer;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

.btn-submit {
  padding: 1rem 3rem;
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s;
  font-family: 'Inter', sans-serif;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 163, 209, 0.4);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .mm-request-view {
    padding: 1rem 0.5rem;
  }

  .form-header h1 {
    font-size: 1.5rem;
  }

  .mm-form {
    padding: 1.5rem 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .btn-submit {
    width: 100%;
  }
}
</style>
